import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Card, Table, Input, Button, Space, Popconfirm, notification, Tag,
  Select, Tooltip, Badge, Empty,
} from "antd";
import {
  PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined,
  ReloadOutlined, PictureOutlined, StarFilled,
} from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import "./ProductsList.scss";
import { buildImageUrl, IMAGE_SIZES } from "../imageUrl";
import { GetMMHProducts, DeleteMMHProduct, GetMasters, USE_MOCK } from "../MMHProductsServices";
import {
  FALLBACK_CATEGORIES,
  PROJECT_TYPE_OPTIONS,
  STATUS_ACTIVE,
  STATUS_INACTIVE,
} from "../constants";

const { Option } = Select;

const EMPTY_FILTERS = { category: "", projectType: "", status: "" };

const ProductsList = () => {
  const history = useHistory();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [failed, setFailed] = useState(false);

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);

  const debounceRef = useRef(null);
  // A slow first request must not overwrite the results of a later one.
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(
    () => () => {
      mountedRef.current = false;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    []
  );

  const fetchProducts = useCallback(
    async ({ page = 1, pageSize = 10, searchValue = "", filterValues = EMPTY_FILTERS } = {}) => {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      setLoading(true);
      setFailed(false);

      try {
        const res = await GetMMHProducts({
          page,
          limit: pageSize,
          search: searchValue,
          filters: filterValues,
        });

        if (!mountedRef.current || requestId !== requestIdRef.current) return;

        const list = res?.data?.data ?? res?.data ?? [];
        const total = res?.data?.total ?? (Array.isArray(list) ? list.length : 0);

        setItems(Array.isArray(list) ? list : []);
        setPagination({ current: page, pageSize, total });
      } catch (error) {
        if (!mountedRef.current || requestId !== requestIdRef.current) return;
        setItems([]);
        setFailed(true);
        notification.error({
          message: "Could not load products",
          description: "Something went wrong while fetching the MMH products list.",
          placement: "bottomRight",
        });
      } finally {
        if (mountedRef.current && requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    []
  );

  useEffect(() => {
    fetchProducts({ page: 1, pageSize: 10 });

    // The category filter offers the same master list the form writes from.
    GetMasters()
      .then((res) => {
        if (mountedRef.current && res?.data?.categories?.length) {
          setCategories(res.data.categories);
        }
      })
      .catch(() => {
        // Falls back to the bundled list.
      });
  }, [fetchProducts]);

  // Search is debounced and always resets to page 1, so a filtered result set
  // can never be paged past its own end.
  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearch(value);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchProducts({ page: 1, pageSize: pagination.pageSize, searchValue: value, filterValues: filters });
    }, 400);
  };

  const handleFilterChange = (key, value) => {
    const nextFilters = { ...filters, [key]: value ?? "" };
    setFilters(nextFilters);
    fetchProducts({ page: 1, pageSize: pagination.pageSize, searchValue: search, filterValues: nextFilters });
  };

  const handleReset = () => {
    setSearch("");
    setFilters(EMPTY_FILTERS);
    fetchProducts({ page: 1, pageSize: pagination.pageSize });
  };

  const handleTableChange = (config) => {
    fetchProducts({
      page: config.current,
      pageSize: config.pageSize,
      searchValue: search,
      filterValues: filters,
    });
  };

  const handleDelete = async (record) => {
    setDeletingId(record.id);
    try {
      await DeleteMMHProduct(record.id);
      notification.success({
        message: "Product deleted",
        description: `"${record.projectName || record.id}" was removed.`,
        placement: "bottomRight",
      });

      // If that was the last row on the page, step back so the table never
      // lands on an empty page.
      const isLastOnPage = items.length === 1 && pagination.current > 1;
      fetchProducts({
        page: isLastOnPage ? pagination.current - 1 : pagination.current,
        pageSize: pagination.pageSize,
        searchValue: search,
        filterValues: filters,
      });
    } catch (error) {
      notification.error({
        message: "Delete failed",
        description: "Could not delete this product, please try again.",
        placement: "bottomRight",
      });
    } finally {
      if (mountedRef.current) setDeletingId(null);
    }
  };

  const hasActiveQuery = Boolean(search) || Object.values(filters).some(Boolean);

  const columns = [
    {
      title: <div className="ap-fsm ap-fwmd">Image</div>,
      dataIndex: "thumbnail",
      key: "thumbnail",
      width: 96,
      render: (thumbnail, record) =>
        thumbnail ? (
          <div className="mmh-list-thumb">
            <img src={buildImageUrl(thumbnail, IMAGE_SIZES.thumb)} alt={record.projectName || "Product"} />
            {record.imageCount > 1 && (
              <span className="mmh-list-thumb-count">
                <PictureOutlined /> {record.imageCount}
              </span>
            )}
          </div>
        ) : (
          <div className="mmh-list-thumb is-empty">
            <PictureOutlined />
          </div>
        ),
    },
    {
      title: <div className="ap-fsm ap-fwmd">Product Name</div>,
      dataIndex: "projectName",
      key: "projectName",
      render: (text, record) => (
        <div className="mmh-list-name">
          <div className="ap-fsm mmh-list-name-main">{text || "-"}</div>
          <div className="mmh-list-name-sub">
            #{record.id}
            {record.projectCode ? ` · ${record.projectCode}` : ""}
          </div>
        </div>
      ),
    },
    {
      title: <div className="ap-fsm ap-fwmd">Category</div>,
      dataIndex: "category",
      key: "category",
      render: (text, record) => (
        <div className="mmh-list-category">
          {record.categoryName ? <Tag color="gold">{record.categoryName}</Tag> : "-"}
          {record.categoryType && <div className="mmh-list-name-sub">{record.categoryType}</div>}
        </div>
      ),
    },
    {
      title: <div className="ap-fsm ap-fwmd">Size</div>,
      key: "size",
      render: (_, record) => (
        <div className="ap-fsm">
          {record.width && record.length ? `${record.width} x ${record.length} ft` : "-"}
          {record.plotArea && <div className="mmh-list-name-sub">{record.plotArea} sq ft</div>}
        </div>
      ),
    },
    {
      title: <div className="ap-fsm ap-fwmd">Client</div>,
      dataIndex: "clientName",
      key: "clientName",
      render: (text, record) => (
        <div className="ap-fsm">
          {text || "-"}
          {record.clientLocation && <div className="mmh-list-name-sub">{record.clientLocation}</div>}
        </div>
      ),
    },
    {
      title: <div className="ap-fsm ap-fwmd">Project Type</div>,
      dataIndex: "projectType",
      key: "projectType",
      render: (value) => (
        <div className="ap-fsm">
          {PROJECT_TYPE_OPTIONS.find((o) => o.value === Number(value))?.label || "-"}
        </div>
      ),
    },
    {
      title: <div className="ap-fsm ap-fwmd">Status</div>,
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (status) =>
        Number(status) === STATUS_ACTIVE ? (
          <Badge status="success" text="Active" />
        ) : (
          <Badge status="default" text="Inactive" />
        ),
    },
    {
      title: <div className="ap-fsm ap-fwmd">Action</div>,
      key: "action",
      fixed: "right",
      width: 190,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit this product">
            <Button
              className="ap-btn-c"
              shape="round"
              icon={<EditOutlined />}
              size="middle"
              onClick={() => history.push(`/home/ap/mmh-products/edit/${record.id}`)}
            >
              Edit
            </Button>
          </Tooltip>
          <Popconfirm
            title={
              <span>
                Delete <strong>{record.projectName || `#${record.id}`}</strong>?
                <br />
                This also removes its {record.imageCount || 0} image
                {record.imageCount === 1 ? "" : "s"}.
              </span>
            }
            okText="Delete"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
            onConfirm={() => handleDelete(record)}
          >
            <Button
              danger
              shape="round"
              icon={<DeleteOutlined />}
              size="middle"
              loading={deletingId === record.id}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="ap-flex ap-flex-center-center ap-w100 mmh-products-list">
      <div className="ap-flex ap-w100">
        <Card style={{ width: "100%" }}>
          <div className="mmh-list-header">
            <div className="mmh-list-title">
              MMH Products
              {USE_MOCK && (
                <Tooltip title="This screen is running on local demo data until the API is ready">
                  <Tag color="blue" className="mmh-mock-tag">
                    <StarFilled /> Demo data
                  </Tag>
                </Tooltip>
              )}
            </div>
            <div className="mmh-list-actions">
              <Input
                placeholder="Search name, code, client or id"
                prefix={<SearchOutlined />}
                value={search}
                onChange={handleSearchChange}
                allowClear
                style={{ width: 260 }}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => history.push("/home/ap/mmh-products/add")}
              >
                Add MMH Product
              </Button>
            </div>
          </div>

          <div className="mmh-list-filters">
            <Select
              value={filters.category || undefined}
              placeholder="All categories"
              onChange={(v) => handleFilterChange("category", v)}
              allowClear
              showSearch
              optionFilterProp="children"
              style={{ width: 220 }}
            >
              {categories.map((c) => (
                <Option value={String(c.id)} key={c.id}>{c.name}</Option>
              ))}
            </Select>

            <Select
              value={filters.projectType || undefined}
              placeholder="All project types"
              onChange={(v) => handleFilterChange("projectType", v)}
              allowClear
              style={{ width: 190 }}
            >
              {PROJECT_TYPE_OPTIONS.map((o) => (
                <Option value={o.value} key={o.value}>{o.label}</Option>
              ))}
            </Select>

            <Select
              value={filters.status === "" ? undefined : filters.status}
              placeholder="All statuses"
              onChange={(v) => handleFilterChange("status", v)}
              allowClear
              style={{ width: 150 }}
            >
              <Option value={STATUS_ACTIVE}>Active</Option>
              <Option value={STATUS_INACTIVE}>Inactive</Option>
            </Select>

            {hasActiveQuery && (
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                Clear filters
              </Button>
            )}

            <span className="mmh-list-count">
              {loading ? "Loading..." : `${pagination.total} product${pagination.total === 1 ? "" : "s"}`}
            </span>
          </div>

          <Table
            rowKey={(record) => record.id}
            columns={columns}
            dataSource={items}
            loading={loading}
            pagination={{ ...pagination, showSizeChanger: true, pageSizeOptions: ["10", "20", "50"] }}
            onChange={handleTableChange}
            scroll={{ x: 1180 }}
            locale={{
              emptyText: failed ? (
                <Empty description="Could not load products">
                  <Button onClick={handleReset} icon={<ReloadOutlined />}>Try again</Button>
                </Empty>
              ) : (
                <Empty
                  description={
                    hasActiveQuery ? "No products match this search" : "No MMH products yet"
                  }
                >
                  {hasActiveQuery ? (
                    <Button onClick={handleReset}>Clear filters</Button>
                  ) : (
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => history.push("/home/ap/mmh-products/add")}>
                      Add your first product
                    </Button>
                  )}
                </Empty>
              ),
            }}
          />
        </Card>
      </div>
    </div>
  );
};

export default ProductsList;
